import React, { useCallback, useEffect } from 'react';
import { Route, useHistory } from 'react-router-dom';
import { useCurrentuser } from '@uplift/contexts';
import { useStore } from '@uplift/hooks';
import { Loader } from '../Loader';
import { ROUTING_CONFIG } from '@uplift/constants';

type ProtectedRouteCheckFn = (user: ReturnType<typeof useCurrentuser>) => boolean;

export type ProtectedRouteCheck =
  | boolean
  | ProtectedRouteCheckFn
  | { fn: ProtectedRouteCheckFn; fallback: (me: ReturnType<typeof useCurrentuser>) => void };

export type ProtectedRouteProps = {
  /**
   * The path to match on
   */
  path: string | string[];

  /**
   * Whether or not the path matching should be exact.
   */
  exact?: boolean;

  /**
   * The URL to redirect to if not logged in
   */
  redirectUrl: string;

  /**
   * Skip login check
   */
  allowAnonymous?: boolean;

  /**
   * Allow user to reach page if all checks pass, redirecting to `fallbackUrl` if no behavior is specified
   */
  allowIf?: ProtectedRouteCheck[];

  /**
   * Prevent user from reaching page if any check fails, redirecting to `fallbackUrl` if no behavior is specified
   */
  blockIf?: ProtectedRouteCheck[];

  /**
   * The URL to redirect to should any checks fail. If not specified, uses `redirectUrl`
   */
  fallbackUrl?: string;
};

const isAbsoluteUrl = (url: string) => {
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
};

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  allowIf,
  blockIf,
  redirectUrl,
  fallbackUrl,
  children,
  ...props
}) => {
  const me = useCurrentuser();
  const { push } = useHistory();
  const { authStore } = useStore();

  useEffect(() => {
    if (props.allowAnonymous) {
      return;
    }

    if (!authStore.isLoggedIn) {
      const params = new URLSearchParams(window.location.search);

      authStore.logout(redirectUrl, {
        redirectAfterLogin: true,
        searchParams: params,
      });
    }
  }, [ authStore, redirectUrl, props.allowAnonymous ]);

  const bail = useCallback(() => {
    const url = fallbackUrl ?? ROUTING_CONFIG.main;

    if (isAbsoluteUrl(url)) {
      window.location.assign(url);
    } else {
      push(url);
    }
  }, [ fallbackUrl, push ]);

  useEffect(() => {
    if (Array.isArray(allowIf)) {
      for (const allow of allowIf) {
        switch (typeof allow) {
          case 'boolean':
            if (!allow) {
              bail();
            }
            break;
          case 'function':
            if (!allow(me)) {
              bail();
            }
            break;
          case 'object':
            if (!allow.fn(me)) {
              allow.fallback(me);
            }
        }
      }
    }
  }, [ allowIf, bail, fallbackUrl, me, push, redirectUrl ]);

  useEffect(() => {
    if (Array.isArray(blockIf)) {
      for (const block of blockIf) {
        switch (typeof block) {
          case 'boolean':
            if (block) {
              bail();
            }
            break;
          case 'function':
            if (block(me)) {
              bail();
            }
            break;
          case 'object':
            if (block.fn(me)) {
              block.fallback(me);
            }
            break;
        }
      }
    }
  }, [ bail, blockIf, fallbackUrl, me, push, redirectUrl ]);

  if (!props.allowAnonymous && !me?.id) {
    // return null if anonymous not allowed and current user is not loaded yet
    // to not show endpoint content if `me` was not loaded
    // and not show content while user is in process of logout
    // me.isLoading is not used because if `me` will be not loaded
    // we still should not show the endpoint
    return <Route {...props}><Loader color="primary" /></Route>;
  }

  return <Route {...props}>{children}</Route>;
};
